import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchFoodMenuAnalysis, type FoodMenuAnalysisItem } from "../foodApi";
import { isIncompleteCost, money, pct } from "../utils/foodFormatters";
import { QueryError } from "../components/QueryError";
import { SimpleTable } from "../components/SimpleTable";
import { SearchInput } from "../components/ListToolbar";
import { UnpricedIngredients } from "../components/UnpricedIngredients";

const STATUS_BG: Record<string, string> = {
  green: "bg-emerald-600",
  yellow: "bg-amber-500",
  red: "bg-red-600",
};

type DisplayStatus = "green" | "yellow" | "red" | "missing" | "unknown";

const STATUS_OPTIONS: { value: DisplayStatus; label: string; dot: string }[] = [
  { value: "green", label: "Green", dot: "bg-emerald-600" },
  { value: "yellow", label: "Yellow", dot: "bg-amber-500" },
  { value: "red", label: "Red", dot: "bg-red-600" },
  { value: "missing", label: "Missing cost", dot: "bg-zinc-600" },
  { value: "unknown", label: "Unknown", dot: "bg-zinc-600" },
];

/** The status a row shows: an incomplete cost reads "missing cost", not its raw status. */
function displayStatus(r: FoodMenuAnalysisItem): DisplayStatus {
  return isIncompleteCost(r) ? "missing" : r.status;
}

const COLUMNS: { key: string; title: string; render: (r: FoodMenuAnalysisItem) => ReactNode }[] = [
  {
    key: "name",
    title: "Name",
    render: (r) => (
      <Link to={`/food/menu/${r.menuItemId}`} className="text-tribal-red hover:underline">
        {r.name}
      </Link>
    ),
  },
  { key: "category", title: "Category", render: (r) => r.category },
  { key: "price", title: "Price", render: (r) => money(r.currentPrice) },
  {
    key: "cost",
    title: "Cost",
    render: (r) =>
      isIncompleteCost(r) ? (
        <div>
          <span className="text-amber-400" title="Some ingredients have no usable cost">
            {money(r.totalCost)} · {r.unpricedIngredientCount} unpriced
          </span>
          <UnpricedIngredients items={r.unpricedIngredients} compact />
        </div>
      ) : (
        money(r.totalCost)
      ),
  },
  { key: "foodCostPct", title: "Food Cost %", render: (r) => pct(r.foodCostPct) },
  { key: "suggested", title: "Suggested Price", render: (r) => money(r.suggestedPrice) },
  {
    key: "status",
    title: "Status",
    render: (r) => (
      <span
        className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold text-white ${STATUS_BG[r.status] ?? "bg-zinc-600"}`}
      >
        {isIncompleteCost(r) ? "missing cost" : r.status}
      </span>
    ),
  },
];

export function AnalysisView() {
  const q = useQuery({ queryKey: ["food", "analysis"], queryFn: fetchFoodMenuAnalysis });
  const [search, setSearch] = useState("");
  const [statuses, setStatuses] = useState<Set<DisplayStatus>>(
    () => new Set(STATUS_OPTIONS.map((o) => o.value)),
  );

  const all = q.data?.menuItems ?? [];
  const counts = useMemo(() => {
    const c = new Map<DisplayStatus, number>();
    for (const r of all) c.set(displayStatus(r), (c.get(displayStatus(r)) ?? 0) + 1);
    return c;
  }, [all]);
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all.filter(
      (r) =>
        statuses.has(displayStatus(r)) &&
        (!term ||
          r.name.toLowerCase().includes(term) ||
          (r.category ?? "").toLowerCase().includes(term)),
    );
  }, [all, search, statuses]);

  function toggleStatus(value: DisplayStatus) {
    setStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-tribal-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading analysis...
      </div>
    );
  }
  if (q.isError || !q.data) {
    return <QueryError message={`Failed to load analysis: ${(q.error as Error).message}`} />;
  }

  const allSelected = statuses.size === STATUS_OPTIONS.length;

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <SearchInput value={search} onChange={setSearch} placeholder="Search menu items" />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-tribal-muted">
          {STATUS_OPTIONS.map((o) => (
            <label key={o.value} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={statuses.has(o.value)}
                onChange={() => toggleStatus(o.value)}
              />
              <span className={`inline-block h-2.5 w-2.5 rounded-full ${o.dot}`} />
              {o.label}
              <span className="text-xs">({counts.get(o.value) ?? 0})</span>
            </label>
          ))}
          <button
            type="button"
            onClick={() =>
              setStatuses(allSelected ? new Set() : new Set(STATUS_OPTIONS.map((o) => o.value)))
            }
            className="rounded bg-white/5 px-2 py-1 text-xs hover:text-white"
          >
            {allSelected ? "Clear all" : "Select all"}
          </button>
        </div>
      </div>
      <p className="mb-2 text-xs text-tribal-muted">
        Showing {rows.length} of {all.length} menu items
      </p>
      <section className="rounded-lg border border-tribal-border bg-tribal-surface">
        <SimpleTable<FoodMenuAnalysisItem>
          rows={rows}
          empty={all.length === 0 ? "No analysis rows found." : "No menu items match these filters."}
          columns={COLUMNS}
        />
      </section>
    </div>
  );
}
